/**
 * Hook wrapping the project-count limit for UI call sites.
 *
 * Deliberately NOT re-exported from `pricing/index.ts` — like AiUsageBadge, it
 * imports useStore, and useStore imports from that barrel. Import it directly:
 * `from '../../pricing/useProjectLimit'`.
 */
import { useStore } from '../store/useStore';
import { usePricingStore } from './pricingStore';
import { UpgradeMessageKey } from './upgradeMessages';
import { FREE_PROJECT_LIMIT } from './pricingTiers';
import { canCreateProject } from './limits';

interface UseProjectLimitResult {
    /** True when the signed-in user has hit their project cap. */
    atLimit: boolean;
    /** The free-tier cap, for display in limit messaging. */
    limit: number;
    /**
     * Call before creating a project. Returns true when creation may proceed;
     * otherwise surfaces the upgrade prompt and returns false.
     */
    ensureCanCreateProject: () => boolean;
}

export function useProjectLimit(): UseProjectLimitResult {
    const user = useStore((s) => s.user);
    const projects = useStore((s) => s.projects);
    const showUpgradePrompt = usePricingStore((s) => s.showUpgradePrompt);

    const atLimit = !canCreateProject(user, projects.length);

    const ensureCanCreateProject = (): boolean => {
        if (!atLimit) return true;
        showUpgradePrompt(UpgradeMessageKey.ProjectLimit);
        return false;
    };

    return { atLimit, limit: FREE_PROJECT_LIMIT, ensureCanCreateProject };
}
